import { mergeTickerUpdates } from '@/utils/mergeTickerUpdates';
/* eslint-disable @typescript-eslint/camelcase */
import { observable, action, computed } from 'mobx';
import reactionWithOldValue from '@/utils/reactionWithOldValue';
import { BigNumber } from 'bignumber.js';
import Axios from 'axios';
import {
  MarkPriceInfo,
  PrecisionMap,
  SymbolConfig,
  TickerInfo,
  FundingRateItem,
  MarkPriceMap,
  RiskFundItem,
  SymbolLeverageBracketItem,
  LeverageBracketItem,
  ScrollPageInfo,
  DepthMergeItem,
  SymbolPrecision,
  CoinIntroductionDetail,
} from './data';
import { API } from '../config/api';
import { PositionItem } from '@/models/position';
import { SCROLL_PAGINATION_LIMIT_DEFAULT } from '@/config/const';
import { createPrecisionView } from '@/utils/digits';
import UnderLyingEnum from '@/@enums/UnderLyingEnum';
import { getBaseUrlBySymbol } from '@/utils/futures';
import { createSliderMarks } from '@/utils/fn';

const defaultTickerInfo = {
  a: '0.00',
  c: '0.00',
  h: '0.00',
  l: '0.00',
  o: '0.00',
  r: '0.00',
  s: '',
  t: 0,
  v: '0.00',
};

const defaultSymbolConfig: SymbolConfig = {
  baseCoin: '',
  baseCoinDisplayPrecision: 0,
  baseCoinPrecision: 0,
  basicMaintMarginRate: 0,
  basicStartMarginRate: 0,
  contractSize: 0,
  contractType: 0,
  cnName: '',
  enName: '',
  initLeverage: 0,
  initPositionType: 'CROSSED',
  makerFee: 0,
  maxEntrusts: 0,
  maxNotional: '',
  maxOpenOrders: 0,
  maxPrice: 0,
  minNotional: '0',
  minPrice: 0,
  minQty: 0,
  minStepPrice: '0.00000001',
  multiplierDown: 1,
  multiplierUp: 1,
  onboardDate: 0,
  pair: '',
  pricePrecision: 0,
  quantityPrecision: 0,
  quoteCoin: '',
  quoteCoinDisplayPrecision: 0,
  quoteCoinPrecision: 0,
  state: 0,
  supportEntrustType: '',
  supportOrderType: '',
  supportPositionType: '',
  supportTimeInForce: '',
  symbol: '',
  takerFee: 0,
  underlyingType: '',
  labels: [],
  depthPrecisionMerge: 0,
  liquidationFee: '0',
  marketTakeBound: '0',
};

/** getList 返回里可能出现的排序字段（与后端字段对齐，命中则按数值升序排） */
const TRADE_PAIR_SORT_KEYS = [
  'sort',
  'sortId',
  'sequence',
  'displaySort',
  'weight',
  'orderNum',
  'rank',
  'seq',
  'orderIndex',
  'listSort',
  'order',
] as const;

function readTradePairSortValue(c: SymbolConfig): number | null {
  const ext = c as SymbolConfig & Record<string, unknown>;
  for (const k of TRADE_PAIR_SORT_KEYS) {
    const v = ext[k];
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v);
  }
  return null;
}

/** 对 getList（或 U+币 concat）结果排序：有 sort 等字段则按字段升序，否则保持接口数组顺序 */
export function orderSymbolConfigsByTradePairSortFields(list: SymbolConfig[]): SymbolConfig[] {
  const decorated = list.map((c, index) => ({
    c,
    index,
    sort: readTradePairSortValue(c),
  }));
  const hasAnySort = decorated.some(d => d.sort !== null);
  if (!hasAnySort) return list.slice();

  return [...decorated]
    .sort((a, b) => {
      if (a.sort !== null && b.sort !== null && a.sort !== b.sort) return a.sort - b.sort;
      if (a.sort !== null && b.sort === null) return -1;
      if (a.sort === null && b.sort !== null) return 1;
      return a.index - b.index;
    })
    .map(d => d.c);
}

/** 合并 U 本位 + 币本位两次 getList 结果 */
export function mergeSymbolConfigListsByTradePairApiOrder(
  uBased: SymbolConfig[],
  coinBased: SymbolConfig[],
): SymbolConfig[] {
  return orderSymbolConfigsByTradePairSortFields([...uBased, ...coinBased]);
}

export default class Ticker {
  @observable currentSymbolIdentity?: string; // 交易对ID

  @observable.ref currentSymbolTicker: TickerInfo;

  @observable uBasedSymbolConfigList: SymbolConfig[];

  @observable coinBasedSymbolConfigList: SymbolConfig[];

  @observable allSymbolConfigList: SymbolConfig[];

  @observable.ref markPriceMap: MarkPriceMap;

  @observable markPriceList: MarkPriceInfo[];

  @observable.ref indexPriceMap: MarkPriceMap;

  @observable indexPriceList: MarkPriceInfo[];

  @observable fundingRateList: FundingRateItem[];

  @observable fundingRateListPageInfo: ScrollPageInfo;

  @observable latestFundingRateInfo: FundingRateItem;

  // Market snapshots are replaced, never mutated in place. Deep observation
  // rebuilds a MobX administration graph for every socket snapshot needlessly.
  @observable.ref tickerList: TickerInfo[];

  @observable riskFundList: RiskFundItem[];

  @observable riskFundListPageInfo: ScrollPageInfo;

  @observable leverageBracketList: SymbolLeverageBracketItem[];

  /** 信息 Tab：当前交易对风险档位详情 */
  @observable infoLeverageBrackets: LeverageBracketItem[];

  /** 信息 Tab：合约交易参数详情 */
  @observable infoSymbolDetail: SymbolConfig | null;

  /** 信息 Tab：币种资料 */
  @observable infoCoinIntroduction: CoinIntroductionDetail | null;

  @observable isFetchingSymbolConfigList: boolean;

  @observable isFetchingLeverageBracketList: boolean;

  @observable isFetchingInfoLeverageBracket: boolean;

  @observable isFetchingInfoSymbolDetail: boolean;

  @observable isFetchingInfoCoinIntroduction: boolean;

  @observable isFetchingTickerList: boolean;

  @observable isFetchingFundRateList: boolean;

  @observable isFetchingRiskFundList: boolean;

  @observable isFetchingIndexPriceList: boolean;

  @observable hasOnboard: boolean;

  @observable uBasedCoinList: string[];

  @observable coinBasedCoinList: string[];

  constructor() {
    this.currentSymbolIdentity = '';
    this.uBasedSymbolConfigList = [];
    this.coinBasedSymbolConfigList = [];
    this.allSymbolConfigList = [];

    this.markPriceList = [];
    this.markPriceMap = {};

    this.indexPriceList = [];
    this.indexPriceMap = {};

    this.fundingRateList = [];
    this.fundingRateListPageInfo = {
      hasPrev: false,
      hasNext: false,
    };
    this.latestFundingRateInfo = {
      nextCollectionTime: 0,
      fundingRate: 0,
      symbol: '',
      collectionInterval: 0,
    };

    this.currentSymbolTicker = defaultTickerInfo;
    this.tickerList = [];

    this.riskFundList = [];
    this.riskFundListPageInfo = {
      hasPrev: false,
      hasNext: false,
    };

    this.leverageBracketList = [];
    this.infoLeverageBrackets = [];
    this.infoSymbolDetail = null;
    this.infoCoinIntroduction = null;

    this.isFetchingSymbolConfigList = false;
    this.isFetchingLeverageBracketList = false;
    this.isFetchingInfoLeverageBracket = false;
    this.isFetchingInfoSymbolDetail = false;
    this.isFetchingInfoCoinIntroduction = false;
    this.isFetchingTickerList = false;
    this.isFetchingFundRateList = false;
    this.isFetchingRiskFundList = false;
    this.isFetchingIndexPriceList = false;

    this.hasOnboard = true;

    this.uBasedCoinList = [];
    this.coinBasedCoinList = [];

    reactionWithOldValue(
      () => this.currentSymbolConfig,
      (currentSymbolConfig, oldValue) => {
        // TODO
      },
    );
  }

  @action.bound
  setLoadingState(key: string, data: boolean) {
    // @ts-ignore
    this[key] = data;
  }

  @action.bound
  setHasOnboard(value: boolean) {
    this.hasOnboard = value;
  }

  @action.bound
  setUBasedSymbolConfigList(data: SymbolConfig[]) {
    this.uBasedSymbolConfigList = data;
    this.allSymbolConfigList = mergeSymbolConfigListsByTradePairApiOrder(data, this.coinBasedSymbolConfigList);
  }

  @action.bound
  setCoinBasedSymbolConfigList(data: SymbolConfig[]) {
    this.coinBasedSymbolConfigList = data;
    this.allSymbolConfigList = mergeSymbolConfigListsByTradePairApiOrder(this.uBasedSymbolConfigList, data);
  }

  @action.bound
  setAllSymbolConfigList(data: SymbolConfig[]) {
    this.allSymbolConfigList = orderSymbolConfigsByTradePairSortFields(data);
  }

  @action.bound
  async fetchSymbolConfigList() {
    this.setLoadingState('isFetchingSymbolConfigList', true);

    const result = await Axios.get(API.SYMBOL_CONFIG_LIST);

    if (Array.isArray(result)) {
      const underlyingType = result[0]?.underlyingType;
      if (underlyingType === UnderLyingEnum.U_BASED) {
        this.setUBasedSymbolConfigList(result);
      } else if (underlyingType === UnderLyingEnum.COIN_BASED) {
        this.setCoinBasedSymbolConfigList(result);
      }
    }

    this.setLoadingState('isFetchingSymbolConfigList', false);
  }

  @action.bound
  async fetchUBasedSymbolConfigList() {
    this.setLoadingState('isFetchingSymbolConfigList', true);

    const result = await Axios.get(API.SYMBOL_CONFIG_LIST, { baseURL: '/f' });

    if (Array.isArray(result)) {
      this.setUBasedSymbolConfigList(result);
    }

    this.setLoadingState('isFetchingSymbolConfigList', false);

    return Promise.resolve(result);
  }

  @action.bound
  async fetchCoinBasedSymbolConfigList() {
    // 项目仅支持 U 本位，不再请求 /d
    this.setCoinBasedSymbolConfigList([]);
    return Promise.resolve([]);
  }

  @action.bound
  getSymbolConfig(symbol: string): SymbolConfig {
    return this.allSymbolConfigList.find((item: SymbolConfig) => symbol === item.symbol) || defaultSymbolConfig;
  }

  @action.bound
  getSymbolConfigByCoin(coin: string): SymbolConfig {
    if (coin === 'usdt') {
      return { baseCoinPrecision: 4, baseCoinDisplayPrecision: 4 } as SymbolConfig;
    }

    return this.allSymbolConfigList.find((item: SymbolConfig) => coin === item.baseCoin) || defaultSymbolConfig;
  }

  @computed get totalSymbolConfigList() {
    return this.allSymbolConfigList;
  }

  @computed get currentSymbolConfig(): SymbolConfig {
    return (
      this.allSymbolConfigList.find((item: SymbolConfig) => item.symbol === this.currentSymbolIdentity) ||
      defaultSymbolConfig
    );
  }

  @computed get precisionMap(): PrecisionMap {
    const result: PrecisionMap = {};
    const configList = this.allSymbolConfigList;
    const length = configList.length;

    for (let i = 0; i < length; i += 1) {
      const {
        symbol,
        contractSize,
        pricePrecision,
        quoteCoinPrecision,
        quoteCoinDisplayPrecision,
        underlyingType,
        baseCoinPrecision,
        baseCoinDisplayPrecision,
      } = configList[i];

      if (underlyingType === UnderLyingEnum.U_BASED) {
        result[symbol] = {
          price: pricePrecision,
          quantity: String(contractSize).split('.')[1]?.length || 0,
          quoteCoin: quoteCoinPrecision,
          quoteCoinDisplay: quoteCoinDisplayPrecision,
        };
      } else if (underlyingType === UnderLyingEnum.COIN_BASED) {
        result[symbol] = {
          price: pricePrecision,
          quantity: baseCoinDisplayPrecision,
          quoteCoin: baseCoinPrecision,
          quoteCoinDisplay: baseCoinDisplayPrecision,
        };
      }
    }
    return result;
  }

  @action.bound
  getPrecision(symbol?: string): SymbolPrecision {
    const { precisionMap } = this;

    if (symbol && Reflect.has(precisionMap, symbol)) {
      return precisionMap[symbol];
    }

    return {
      price: 4,
      quantity: 4,
      quoteCoin: 4,
      quoteCoinDisplay: 4,
    };
  }

  @computed get currentSymbolPrecision(): SymbolPrecision {
    const { currentSymbolIdentity, precisionMap } = this;

    if (currentSymbolIdentity && Reflect.has(precisionMap, currentSymbolIdentity)) {
      return precisionMap[currentSymbolIdentity];
    }

    return {
      price: 4,
      quantity: 4,
      quoteCoin: 4,
      quoteCoinDisplay: 4,
    };
  }

  @action.bound
  setCurrentSymbolIdentity(data: string) {
    this.currentSymbolIdentity = data;
    sessionStorage.setItem('symbol', data || '');
  }

  @action.bound
  setMarkPriceList(data: MarkPriceInfo[]) {
    this.markPriceList = data;
  }

  // 某个交易对的标记价格
  @action.bound
  async fetchMarkPriceList({ symbol }: { symbol: string }) {
    this.setLoadingState('isFetchingIndexPriceList', true);

    const limit = 50;
    const endTime = +new Date();
    const startTime = endTime - limit * 60 * 1000;

    const result = await Axios.get(API.TICKER_KLINE, {
      params: {
        symbol: `${symbol?.split('_')[0]}_mark`,
        endTime,
        startTime,
        limit,
        interval: '1m',
      },
      baseURL: getBaseUrlBySymbol(symbol),
    });

    if (Array.isArray(result)) {
      this.setMarkPriceList(result);
    }

    this.setLoadingState('isFetchingIndexPriceList', false);
  }

  @action.bound
  setMarkPriceMap(data: MarkPriceMap) {
    const origin = this.markPriceMap;
    if (!Object.keys(data).some(symbol => origin[symbol] !== data[symbol])) return;
    this.markPriceMap = { ...origin, ...data };
  }

  @computed get latestMarkPrice() {
    // @ts-ignore
    return this.markPriceMap[this.currentSymbolIdentity] || '0.00';
  }

  // 所有交易对的最新标记价格
  @action.bound
  async fetchMarkPriceMap() {
    const result = await Axios.get(API.TICKER_MARK_PRICE);

    if (Array.isArray(result)) {
      const temp: MarkPriceMap = {};

      for (let i = 0; i < result.length; i += 1) {
        const item = result[i];
        temp[item.s] = item.p;
      }

      this.setMarkPriceMap(temp);
    }
  }

  @action.bound
  setIndexPriceList(data: MarkPriceInfo[]) {
    this.indexPriceList = data;
  }

  // 某个交易对的标记价格
  @action.bound
  async fetchIndexPriceList({ symbol }: { symbol: string }) {
    this.setLoadingState('isFetchingIndexPriceList', true);

    const limit = 50;
    const endTime = +new Date();
    const startTime = endTime - limit * 60 * 1000;

    const result = await Axios.get(API.TICKER_KLINE, {
      params: {
        symbol,
        endTime,
        startTime,
        limit,
        interval: '1m',
      },
      baseURL: getBaseUrlBySymbol(symbol),
    });

    if (Array.isArray(result)) {
      this.setIndexPriceList(result);
    }

    this.setLoadingState('isFetchingIndexPriceList', false);
  }

  @action.bound
  setIndexPriceMap(data: MarkPriceMap) {
    const origin = this.indexPriceMap;
    if (!Object.keys(data).some(symbol => origin[symbol] !== data[symbol])) return;
    this.indexPriceMap = { ...origin, ...data };
  }

  @computed get latestIndexPrice() {
    // @ts-ignore
    return this.indexPriceMap[this.currentSymbolIdentity] || '0.00';
  }

  @computed get availablePrice() {
    const markPrice = this.latestMarkPrice;
    const indexPrice = this.latestIndexPrice;
    return new BigNumber(markPrice).gt(0) ? markPrice : indexPrice;
  }

  // 所有交易对的最新指数价格
  @action.bound
  async fetchIndexPriceMap() {
    const result = await Axios.get(API.TICKER_INDEX_PRICE);

    if (Array.isArray(result)) {
      const temp: MarkPriceMap = {};

      for (let i = 0; i < result.length; i += 1) {
        const item = result[i];
        temp[item.s] = item.p;
      }

      this.setIndexPriceMap(temp);
    }
  }

  // 资金费率
  @action.bound
  setLatestFundingRateInfo(data: FundingRateItem) {
    this.latestFundingRateInfo = data;
  }

  @action.bound
  async fetchLatestFundingRate({ symbol }: { symbol: string }) {
    const result = await Axios.get(API.TICKER_FUNDING_RATE, {
      params: {
        symbol,
      },
    });

    if (result) {
      this.setLatestFundingRateInfo(result as any);
    }
  }
  // socket更新资金费率 /5s；基础利率、资金费率上下限以 HTTP 为准，WS 不覆盖
  @action.bound
  setLatestFundingRateInfoByWS(data: FundingRateItem) {
    const { interestRate, fundingChangeUp, fundingChangeDown } = this.latestFundingRateInfo || {};
    this.latestFundingRateInfo = {
      ...data,
      interestRate,
      fundingChangeUp,
      fundingChangeDown,
    };
  }
  @action.bound
  setFundingRateList(data: FundingRateItem[]) {
    this.fundingRateList = data;
  }

  @action.bound
  setFundingRateListPageInfo(data: ScrollPageInfo) {
    const origin = { ...this.fundingRateListPageInfo };
    this.fundingRateListPageInfo = { ...origin, ...data };
  }

  @action.bound
  async fetchFundingRateList({
    symbol,
    direction,
    limit = SCROLL_PAGINATION_LIMIT_DEFAULT,
    id,
  }: {
    symbol: string;
    direction: ScrollPaginationDirection;
    limit?: number;
    id?: number;
  }) {
    this.setLoadingState('isFetchingFundRateList', true);

    const config: any = {
      params: {
        symbol,
        direction,
        limit,
        id,
      },
    };

    if (symbol) {
      config.baseURL = getBaseUrlBySymbol(symbol);
    }

    const result: any = await Axios.get(API.TICKER_FUNDING_RATE_RECORD, config);

    if (result) {
      const { hasNext, hasPrev, items } = result;

      this.setFundingRateList(items || []);
      this.setFundingRateListPageInfo({ hasPrev, hasNext });
    }

    this.setLoadingState('isFetchingFundRateList', false);
  }

  @action.bound
  setCurrentSymbolTicker(data: TickerInfo) {
    this.currentSymbolTicker = data;
  }

  @action.bound
  async fetchCurrentSymbolTicker(params: { symbol: string }) {
    const { symbol } = params;

    if (!symbol) return;

    const result: any = await Axios.get(API.TICKER_CURRENT_SYMBOL, {
      params: {
        symbol,
      },
    });

    if (result) {
      this.setCurrentSymbolTicker(result);
    } else {
      this.setCurrentSymbolTicker(defaultTickerInfo);
    }
  }

  @action
  setTickerList(data: TickerInfo[]) {
    this.tickerList = data;
  }

  @action.bound
  async fetchTickerList() {
    this.setLoadingState('isFetchingTickerList', true);
    const result = await Axios.get(API.TICKER_LIST);
    if (Array.isArray(result)) {
      this.setTickerList(result);
    }
    this.setLoadingState('isFetchingTickerList', false);
  }

  @computed get symbolCurrentPriceMap() {
    const result: { [key: string]: string | number } = {};
    this.tickerList.forEach((item: TickerInfo) => {
      result[item.s] = item.c;
    });

    return result;
  }

  // ws 更新行情列表
  @action.bound
  setSymbolTickerByWS(data: { [key: string]: TickerInfo }) {
    const updates = Object.values(data);
    const next = mergeTickerUpdates(this.tickerList, updates);
    if (next === this.tickerList) return;
    this.setTickerList(next);
    const current = next.find(ticker => ticker.s === this.currentSymbolIdentity);
    if (current) this.setCurrentSymbolTicker(current);
  }

  // 风险保障基金
  @action.bound
  setRiskFundList(data: RiskFundItem[]) {
    this.riskFundList = data;
  }

  @action.bound
  setRiskFundListPageInfo(data: ScrollPageInfo) {
    const origin = { ...this.riskFundListPageInfo };
    this.riskFundListPageInfo = { ...origin, ...data };
  }

  @action.bound
  async fetchRiskFundList({
    symbol,
    direction,
    limit = SCROLL_PAGINATION_LIMIT_DEFAULT,
    id,
  }: {
    symbol: string;
    direction: ScrollPaginationDirection;
    limit?: number;
    id?: number;
  }) {
    this.setLoadingState('isFetchingRiskFundList', true);

    const config: any = {
      params: {
        symbol,
        direction,
        limit,
        id,
      },
    };

    if (symbol) {
      config.baseURL = getBaseUrlBySymbol(symbol);
    }

    const result: any = await Axios.get(API.RISK_FUND_LIST, config);

    if (result) {
      const { hasNext, hasPrev, items } = result;

      this.setRiskFundList(items || []);
      this.setRiskFundListPageInfo({ hasPrev, hasNext });
    }

    this.setLoadingState('isFetchingRiskFundList', false);
  }

  // 杠杆分层
  @action.bound
  setLeverageBracketList(data: SymbolLeverageBracketItem[]) {
    this.leverageBracketList = data;
  }

  @action.bound
  async fetchLeverageBracketList() {
    this.setLoadingState('isFetchingLeverageBracketList', true);
    const result: SymbolLeverageBracketItem[] = await Axios.get(API.LEVERAGE_BRACKET_LIST);

    if (result) {
      this.setLeverageBracketList(result);
    }
    this.setLoadingState('isFetchingLeverageBracketList', false);
  }

  @action.bound
  clearInfoPanelData() {
    this.infoLeverageBrackets = [];
    this.infoSymbolDetail = null;
    this.infoCoinIntroduction = null;
  }

  /** 信息 Tab：按登录态拉取风险档位详情 */
  @action.bound
  async fetchInfoLeverageBracketDetail(params: { symbol: string; isLogin?: boolean }) {
    const symbol = params.symbol?.toLowerCase();
    if (!symbol) {
      this.infoLeverageBrackets = [];
      return;
    }

    this.setLoadingState('isFetchingInfoLeverageBracket', true);
    try {
      const url = params.isLogin ? API.LEVERAGE_BRACKET_DETAIL_USER : API.LEVERAGE_BRACKET_DETAIL_PUBLIC;
      const result: any = await Axios.get(url, { params: { symbol } });
      const brackets = result?.leverageBrackets || result?.brackets || (Array.isArray(result) ? result : []);
      this.infoLeverageBrackets = Array.isArray(brackets) ? brackets : [];
    } catch (e) {
      this.infoLeverageBrackets = [];
    }
    this.setLoadingState('isFetchingInfoLeverageBracket', false);
  }

  /** 信息 Tab：合约交易参数详情（必须单独请求） */
  @action.bound
  async fetchInfoSymbolDetail(params: { symbol: string }) {
    const symbol = params.symbol?.toLowerCase();
    if (!symbol) {
      this.infoSymbolDetail = null;
      return;
    }

    this.setLoadingState('isFetchingInfoSymbolDetail', true);
    try {
      const result: SymbolConfig = await Axios.get(API.SYMBOL_TRADE_PAIR_DETAIL, { params: { symbol } });
      this.infoSymbolDetail = result || null;
    } catch (e) {
      this.infoSymbolDetail = null;
    }
    this.setLoadingState('isFetchingInfoSymbolDetail', false);
  }

  /** 信息 Tab：币种资料 */
  @action.bound
  async fetchInfoCoinIntroduction(params: { currency: string }) {
    const currency = params.currency?.toUpperCase();
    if (!currency) {
      this.infoCoinIntroduction = null;
      return;
    }

    this.setLoadingState('isFetchingInfoCoinIntroduction', true);
    try {
      const result: CoinIntroductionDetail = await Axios.get(API.COIN_INTRODUCTION_DETAIL, {
        params: { currency },
      });
      this.infoCoinIntroduction = result || null;
    } catch (e) {
      this.infoCoinIntroduction = null;
    }
    this.setLoadingState('isFetchingInfoCoinIntroduction', false);
  }

  @computed get currentSymbolLeverageBrackets(): LeverageBracketItem[] {
    const symbol = this.currentSymbolIdentity;
    const result = this.leverageBracketList.find(item => item.symbol === symbol);

    if (result) {
      // 按 maxLeverage 降序排列（接口字段为 string，与 getPositionBracket 一致用 BigNumber 比较）
      return [...result.leverageBrackets].sort((a, b) =>
        new BigNumber(b.maxLeverage).comparedTo(new BigNumber(a.maxLeverage)),
      );
    }

    return [];
  }

  @computed get leverageSliderConfig(): { maxMultiple: number; minMultiple: number; marks: any } {
    const firsLv = this.currentSymbolLeverageBrackets[0];
    const max = firsLv?.maxLeverage || 100;
    const min = firsLv?.minLeverage || 1;

    return {
      maxMultiple: new BigNumber(max).toNumber(),
      minMultiple: new BigNumber(min).toNumber(),
      marks: createSliderMarks(max, min),
    };
  }

  // 获取持仓所处的的风险档位
  @action.bound
  getPositionBracket(position: PositionItem): LeverageBracketItem {
    const { symbol, positionSize, entryPrice, leverage } = position;
    const { getSymbolConfig, leverageBracketList, getSymbolUnderlyingType } = this;
    const currentSymbolConfig = getSymbolConfig(symbol);
    const { contractSize } = currentSymbolConfig;

    // if (contractSize) {
    //
    //
    //   const underlyingType = getSymbolUnderlyingType(symbol);
    //   let nominalValueBN: any = new BigNumber(0);
    //
    //   if (new BigNumber(positionSize).gt(0)) {
    //     if (underlyingType === UnderLyingEnum.U_BASED) {
    //       nominalValueBN = new BigNumber(entryPrice).times(positionSize).times(contractSize);
    //     } else if (underlyingType === UnderLyingEnum.COIN_BASED) {
    //       nominalValueBN = new BigNumber(positionSize).times(contractSize).div(entryPrice);
    //     }
    //   }
    //
    //
    // }

    const symbolBrackets = leverageBracketList.find(item => item.symbol === symbol)?.leverageBrackets;
    // 档位需按 maxLeverage 降序排列后匹配；接口若为升序会错误命中「最高档」得到过低的维持保证金率
    const sorted =
      symbolBrackets == null
        ? []
        : [...symbolBrackets].sort((a, b) =>
          new BigNumber(b.maxLeverage).comparedTo(new BigNumber(a.maxLeverage)),
        );
    const result =
      sorted.find((item, index, array) => {
        const currentMaxLeverage = new BigNumber(item.maxLeverage);
        const nextMaxLeverage = index < array.length - 1 ? new BigNumber(array[index + 1].maxLeverage) : null;

        return (
          new BigNumber(leverage).lte(currentMaxLeverage) &&
          (!nextMaxLeverage || new BigNumber(leverage).gt(nextMaxLeverage))
        );
      }) || ({} as LeverageBracketItem);

    return result;
  }

  // 与 getPositionBracket 同一套档位匹配规则（按杠杆落在哪一档），避免 findLast 与数组排序假设不一致
  @action.bound
  getLeverageMaxBracket(leverage: number): LeverageBracketItem | undefined {
    const symbol = this.currentSymbolIdentity;
    if (!symbol) return undefined;
    const b = this.getPositionBracket({ symbol, leverage } as PositionItem);
    return b?.maxLeverage != null && b.maxLeverage !== '' ? b : undefined;
  }

  // 当前交易对档位深度
  @computed get currentSymbolDepthPrecisionMergeList(): DepthMergeItem[] {
    const { depthPrecisionMerge, pricePrecision } = this.currentSymbolConfig;
    const base = new BigNumber(createPrecisionView(pricePrecision));
    const result = [];

    if (depthPrecisionMerge && !base.isNaN()) {
      for (let i = 1; i <= depthPrecisionMerge; i += 1) {
        const label = base.times(10 ** (i - 1));
        const precision = label.toFixed().split('.')[1]?.length || 0;
        const item = { label: label.toFixed(), value: i, base: label.toNumber(), precision };
        result.push(item);
      }
    }

    return result;
  }

  @action.bound
  getSymbolUnderlyingType(symbol: string): UnderLyingEnum {
    return this.allSymbolConfigList.find((item: SymbolConfig) => symbol?.toLowerCase() === item.symbol)
      ?.underlyingType as UnderLyingEnum;
  }

  @action
  setUBasedCoinList(data: string[]) {
    this.uBasedCoinList = data;
  }

  @action
  setCoinBasedCoinList(data: string[]) {
    this.coinBasedCoinList = data;
  }

  @action.bound
  async fetchCoinList() {
    const uBasedResult = await Axios.get(API.SYMBOL_COIN_LIST, { baseURL: '/f' });

    if (Array.isArray(uBasedResult)) {
      this.setUBasedCoinList(uBasedResult);
    }
    this.setCoinBasedCoinList([]);
  }

  @computed get latestPriceMap() {
    const tickerList = this.tickerList;
    const result: any = {};

    for (let i = 0; i < tickerList.length; i += 1) {
      const item = tickerList[i];
      result[item.s] = item.c;
    }

    return result;
  }
}
